import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';

import { CaseModel } from 'models/CaseModel';
import { CommentModel } from 'models/CommentModel';
import { ExamGridModel } from 'models/ExamGridModel';
import { FileModel } from 'models/FileModel';
import { TaskModel } from 'models/TaskModel';

import { Button, Input, TextArea } from 'core/ui';

import { useApiClient } from 'features/api';
import { ImgProxy } from 'features/file/fragments/ImgProxy';

import { getFileIcon, sortFilesByDate } from '../services/file-category-helper';

// For UTC parsing and conversion

dayjs.extend(relativeTime);
dayjs.extend(utc);

export type TimelineTab = 'All' | 'Unread' | 'Tasks' | 'Messages';

export interface TimelineItem {
  id: string; // Composite key like type-id
  type: 'exam' | 'file' | 'comment' | 'task';
  date: string;
  exam?: ExamGridModel;
  file?: FileModel;
  comment?: CommentModel;
  task?: TaskModel;
  userName?: string; // For comments
  message?: string; // For comments
  isUnread?: boolean; // Added for client-side optimistic update
}

interface ActivityTimelineProps {
  patientId: number;
  caseId?: number | null; // For case-specific activities like tasks/comments on a case
  exams: ExamGridModel[];
  files: FileModel[];
  tasks: TaskModel[]; // Tasks will be fetched at a higher level and passed in
  // onFileView: (file: FileModel) => void;
  // onExamView: (exam: ExamGridModel) => void;
  // For now, keep click handlers internal or pass specific ones
  onItemClick?: (item: TimelineItem) => void; // Generic click handler for now
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ patientId, caseId, exams, files, tasks, onItemClick }) => {
  const apiClient = useApiClient();
  const [comments, setComments] = useState<CommentModel[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [activeTab, setActiveTab] = useState<TimelineTab>('All');

  // Local state for timeline items to manage isUnread optimistically
  const [displayableTimelineItems, setDisplayableTimelineItems] = useState<TimelineItem[]>([]);

  const logActivityView = useCallback(
    async (itemType: string, itemId: number) => {
      try {
        await apiClient.activityViewLogClient.logActivityView({ entityType: itemType, entityId: itemId }, { authMode: 'msal-required' });
        // console.log(`View logged for ${itemType} - ${itemId}`);
      } catch (error) {
        console.error(`Error logging view for ${itemType} - ${itemId}:`, error);
      }
    },
    [apiClient.activityViewLogClient],
  );

  // TODO: Determine entityType and entityId for fetching/posting comments.
  // For now, let's assume comments are related to the patient if no caseId, or to the case if caseId is present.
  const commentEntityType = caseId ? 'Case' : 'Patient';
  const commentEntityId = caseId ?? patientId;

  useEffect(() => {
    const fetchComments = async () => {
      if (!commentEntityId) return;
      setIsLoadingComments(true);
      try {
        const fetchedComments = await apiClient.commentClient.getComments(commentEntityType, commentEntityId);
        console.log('Fetched Comments from API:', JSON.stringify(fetchedComments, null, 2)); // Log the raw API response
        // When backend provides isUnread, it will be used here directly
        setComments(fetchedComments.map((c) => ({ ...c, isUnread: c.isUnread ?? true })) || []);
      } catch (error) {
        console.error('Error fetching comments:', error);
        setComments([]);
      } finally {
        setIsLoadingComments(false);
      }
    };
    fetchComments();
  }, [apiClient.commentClient, commentEntityType, commentEntityId]);

  // Effect to build the initial displayableTimelineItems with isUnread flags
  useEffect(() => {
    const examItems: TimelineItem[] = exams.map((exam) => ({
      id: `exam-${exam.id}`,
      type: 'exam',
      date: exam.uploadedDate || exam.studyDate || '',
      exam,
      isUnread: exam.isUnread ?? true, // Default to true if backend doesn't provide
    }));

    const fileItems: TimelineItem[] = files.map((file) => ({
      id: `file-${file.id}`,
      type: 'file',
      date: file.created || file.modified || '',
      file,
      isUnread: file.isUnread ?? true, // Default to true
    }));

    const commentItems: TimelineItem[] = comments.map((comment) => ({
      id: `comment-${comment.id}`,
      type: 'comment',
      date: comment.timestamp,
      comment,
      userName: comment.userName,
      message: comment.commentText,
      isUnread: comment.isUnread ?? true, // Default to true
    }));
    console.log('Mapped Comment Items for Timeline:', JSON.stringify(commentItems, null, 2));

    const taskItems: TimelineItem[] = tasks.map((task) => ({
      id: `task-${task.id}`,
      type: 'task',
      date: task.lastUpdated,
      task,
      isUnread: task.isUnread ?? true, // Default to true
    }));

    const combinedUnfiltered = [...examItems, ...fileItems, ...commentItems, ...taskItems];
    console.log('Combined Timeline Items (Before Date Filter):', JSON.stringify(combinedUnfiltered, null, 2));

    const combined = combinedUnfiltered.filter((item) => !!item.date);
    console.log('Combined Timeline Items (After Date Filter):', JSON.stringify(combined, null, 2));

    setDisplayableTimelineItems(combined);
  }, [exams, files, comments, tasks]);

  const filteredAndSortedItems = useMemo(() => {
    let itemsToDisplay = [...displayableTimelineItems];

    if (activeTab === 'Unread') {
      itemsToDisplay = itemsToDisplay.filter((item) => item.isUnread);
    } else if (activeTab === 'Tasks') {
      itemsToDisplay = itemsToDisplay.filter((item) => item.type === 'task');
    } else if (activeTab === 'Messages') {
      itemsToDisplay = itemsToDisplay.filter((item) => item.type === 'comment');
    }

    itemsToDisplay.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
    return itemsToDisplay;
  }, [displayableTimelineItems, activeTab]);

  const unreadCount = useMemo(() => displayableTimelineItems.filter((item) => item.isUnread).length, [displayableTimelineItems]);
  const tasksCount = useMemo(() => displayableTimelineItems.filter((item) => item.type === 'task').length, [displayableTimelineItems]);
  const messagesCount = useMemo(() => displayableTimelineItems.filter((item) => item.type === 'comment').length, [displayableTimelineItems]);

  const handlePostComment = async () => {
    if (!newCommentText.trim() || !commentEntityId) return;
    try {
      const newCommentData = await apiClient.commentClient.postComment(
        { entityType: commentEntityType, entityId: commentEntityId, commentText: newCommentText.trim() },
        { authMode: 'msal-required' },
      );
      // Assume new comments are initially unread for others, but read for the poster (or handle as per business logic)
      const newCommentForState: CommentModel = { ...newCommentData, isUnread: true };
      setComments((prev) => [newCommentForState, ...prev]);
      setNewCommentText('');
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  const handleItemClickInternal = (item: TimelineItem) => {
    let entityTypeForLog: string | null = null;
    let entityIdForLog: number | null = null;

    if (item.type === 'exam' && item.exam?.id) {
      entityTypeForLog = 'Exam';
      entityIdForLog = item.exam.id;
    } else if (item.type === 'file' && item.file?.id) {
      entityTypeForLog = 'File';
      entityIdForLog = item.file.id;
    } else if (item.type === 'comment' && item.comment?.id) {
      entityTypeForLog = 'Comment';
      entityIdForLog = item.comment.id;
    } else if (item.type === 'task' && item.task?.id) {
      entityTypeForLog = 'Task';
      entityIdForLog = item.task.id;
    }

    if (entityTypeForLog && entityIdForLog !== null) {
      logActivityView(entityTypeForLog, entityIdForLog);
      // Optimistically update isUnread status
      setDisplayableTimelineItems((prevItems) => prevItems.map((prevItem) => (prevItem.id === item.id ? { ...prevItem, isUnread: false } : prevItem)));
    }

    if (onItemClick) {
      onItemClick(item);
    }
  };

  const renderTimelineItemIcon = (item: TimelineItem) => {
    switch (item.type) {
      case 'comment':
        return (
          <StyledUserIcon>
            <i className="k-icon k-i-user" />
          </StyledUserIcon>
        );
      case 'exam': {
        const { exam } = item;
        if (exam?.files && exam.files.length > 0) {
          const sorted = sortFilesByDate(exam.files);
          const thumb = sorted[0].thumbnailLocation;
          if (thumb) {
            return (
              <StyledThumbIcon onClick={() => handleItemClickInternal(item)}>
                <ImgProxy fileId={sorted[0].id} thumbnailUrl={thumb} dateModified={sorted[0].modified} isReadOnly height="100%" width="100%" />
              </StyledThumbIcon>
            );
          }
        }
        return (
          <StyledExamIcon onClick={() => handleItemClickInternal(item)}>
            <i className="k-icon k-i-folder-image" />
          </StyledExamIcon>
        );
      }
      case 'file': {
        const { file } = item;
        if (file?.thumbnailUrl) {
          return (
            <StyledThumbIcon onClick={() => handleItemClickInternal(item)}>
              <ImgProxy fileId={file.id} thumbnailUrl={file.thumbnailUrl} dateModified={file.modified} isReadOnly height="100%" width="100%" />
            </StyledThumbIcon>
          );
        }
        return <StyledFileIconLarge onClick={() => handleItemClickInternal(item)}>{getFileIcon(file?.fileType || 'unknown')}</StyledFileIconLarge>;
      }
      case 'task':
        return (
          <StyledTaskIcon onClick={() => handleItemClickInternal(item)}>
            <i className="k-icon k-i-track-changes" />
          </StyledTaskIcon>
        );
      default:
        return null;
    }
  };

  const renderTimelineItemContent = (item: TimelineItem) => {
    const commonClickHandler = () => handleItemClickInternal(item);
    switch (item.type) {
      case 'comment':
        return (
          <div>
            <strong>{item.userName}:</strong> {item.message}
          </div>
        );
      case 'exam':
        return (
          <StyledTimelineClickableContent onClick={commonClickHandler}>
            <strong>Exam:</strong> {item.exam?.serviceDescription || 'No Description'}
            {item.exam?.description && <StyledSubText> - {item.exam.description}</StyledSubText>}
          </StyledTimelineClickableContent>
        );
      case 'file':
        return (
          <StyledTimelineClickableContent onClick={commonClickHandler}>
            <strong>File Uploaded:</strong> {item.file?.fileName || 'Unnamed'}
          </StyledTimelineClickableContent>
        );
      case 'task':
        return (
          <StyledTimelineClickableContent onClick={commonClickHandler}>
            <strong>Task:</strong> {item.task?.description || 'No Description'} (<em>{item.task?.status}</em>)
          </StyledTimelineClickableContent>
        );
      default:
        return null;
    }
  };

  return (
    <StyledTimelineContainer>
      <StyledTabsHeader>
        <StyledTabButton active={activeTab === 'All'} onClick={() => setActiveTab('All')}>
          All
        </StyledTabButton>
        <StyledTabButton active={activeTab === 'Unread'} onClick={() => setActiveTab('Unread')}>
          Unread {unreadCount > 0 && `(${unreadCount})`}
        </StyledTabButton>
        <StyledTabButton active={activeTab === 'Tasks'} onClick={() => setActiveTab('Tasks')}>
          Tasks {tasksCount > 0 && `(${tasksCount})`}
        </StyledTabButton>
        <StyledTabButton active={activeTab === 'Messages'} onClick={() => setActiveTab('Messages')}>
          Messages {messagesCount > 0 && `(${messagesCount})`}
        </StyledTabButton>
      </StyledTabsHeader>

      {(activeTab === 'All' || activeTab === 'Messages' || activeTab === 'Unread') && (
        <StyledCommentPostContainer>
          <TextArea
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value ?? '')}
            placeholder={`Post a comment to ${commentEntityType}...`}
            rows={3}
            style={{ flexGrow: 1, marginRight: '10px' }}
          />
          <Button onClick={handlePostComment} disabled={!newCommentText.trim() || isLoadingComments}>
            Post
          </Button>
        </StyledCommentPostContainer>
      )}

      {isLoadingComments && (activeTab === 'Messages' || activeTab === 'All' || activeTab === 'Unread') && (
        <StyledLoadingIndicator>Loading comments...</StyledLoadingIndicator>
      )}

      {filteredAndSortedItems.map((item) => (
        <StyledTimelineItem key={item.id} isUnread={item.isUnread}>
          <StyledTimelineIconWrapper>{renderTimelineItemIcon(item)}</StyledTimelineIconWrapper>
          <StyledTimelineContent>
            <StyledTimelineDate>
              {dayjs.utc(item.date).local().format('MM/DD/YYYY h:mm A')} ({dayjs.utc(item.date).fromNow()})
            </StyledTimelineDate>
            {renderTimelineItemContent(item)}
          </StyledTimelineContent>
        </StyledTimelineItem>
      ))}
      {filteredAndSortedItems.length === 0 && !isLoadingComments && (
        <StyledEmptyMessage>
          {activeTab === 'Unread'
            ? 'No unread activity.'
            : activeTab === 'Tasks'
              ? 'No tasks found.'
              : activeTab === 'Messages'
                ? 'No messages found.'
                : 'No activity to display.'}
        </StyledEmptyMessage>
      )}
    </StyledTimelineContainer>
  );
};

export default ActivityTimeline;

// --- Styled Components (copied and adapted from PatientDashboard) ---

const StyledTimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%; // Allow it to take full height of its container
  overflow-y: auto; // Add scroll for long timelines
`;

const StyledTabsHeader = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
`;

const StyledTabButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px; // Adjusted padding
  border-radius: 4px;
  border: 1px solid ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.palette.grayscale[3])};
  background-color: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.palette.white)};
  color: ${({ active, theme }) => (active ? theme.colors.palette.white : theme.colors.palette.grayscale[7])};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  white-space: nowrap; // Prevent counts from wrapping

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.palette.grayscale[1])};
  }
`;

const StyledCommentPostContainer = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
`;

const StyledTimelineItem = styled.div<{ isUnread?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-left: 3px solid ${({ theme, isUnread }) => (isUnread ? theme.colors.primary : theme.colors.palette.grayscale[3])};
  padding-left: 12px;
  position: relative;
  background-color: ${({ theme, isUnread }) => (isUnread ? theme.colors.palette.blues[0] : 'transparent')}; // Subtle background for unread
  border-radius: 4px;

  &:before {
    content: '';
    position: absolute;
    left: -8px; // Adjusted for thicker line
    width: 12px;
    height: 12px;
    background-color: ${({ theme, isUnread }) => (isUnread ? theme.colors.primary : theme.colors.palette.grayscale[3])};
    border-radius: 50%;
    top: 6px;
    border: 2px solid ${({ theme }) => theme.colors.palette.white};
  }
`;

const StyledTimelineIconWrapper = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%; // Make all icons circular by default
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[2]};
`;

const StyledTimelineContent = styled.div`
  flex: 1;
  padding-top: 2px; // Align text better with icon center
`;

const StyledTimelineDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.palette.grayscale[7]};
  margin-bottom: 4px;
`;

const StyledUserIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffc107; // Keep user icon color distinct
  width: 100%;
  height: 100%;
  color: #fff;
  .k-icon {
    font-size: 20px;
  }
`;

const StyledExamIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #03a9f4;
  width: 100%;
  height: 100%;
  /* border-radius: 4px; */ // Handled by wrapper
  color: #fff;
  .k-icon {
    font-size: 20px;
  }
`;

const StyledFileIconLarge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #9c27b0;
  width: 100%;
  height: 100%;
  /* border-radius: 4px; */ // Handled by wrapper
  color: #fff;
  font-size: 20px;
`;

const StyledTaskIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #4caf50; // Green for tasks
  width: 100%;
  height: 100%;
  color: #fff;
  .k-icon {
    font-size: 20px;
  }
`;

const StyledThumbIcon = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* border-radius: 4px; */ // Handled by wrapper
  cursor: pointer;
  img {
    object-fit: cover;
  }
`;

const StyledEmptyMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  font-style: italic;
`;

const StyledLoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.colors.palette.grayscale[7]};
`;

const StyledTimelineClickableContent = styled.div`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const StyledSubText = styled.span`
  font-size: 0.9em;
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
`;
